import { lazy } from "react";
import { createBrowserRouter, type RouteObject } from "react-router-dom";
import { isVideoAnalysisEnabled, isVideoSearchEnabled } from "@/config/features";
import { RouteFrame } from "@/components/site/RouteFrame";
import { RouteError } from "@/components/site/RouteError";
import NotFoundPage from "@/routes/not-found-page";
import { LandingPage } from "@/components/landing/LandingPage";

const AboutPage = lazy(() => import("@/routes/about/page"));
const AgentPage = lazy(() => import("@/routes/agents/[slug]/page"));
const AgentsPage = lazy(() => import("@/routes/agents/page"));
const BlogPostPage = lazy(() => import("@/routes/blog/[slug]/page"));
const BlogPage = lazy(() => import("@/routes/blog/page"));
const ContactPage = lazy(() => import("@/routes/contact/page"));
const EarlyAccessPage = lazy(() => import("@/routes/early-access/page"));
const HelpPage = lazy(() => import("@/routes/help/page"));
const IntegrationsPage = lazy(() => import("@/routes/integrations/page"));
const PricingPage = lazy(() => import("@/routes/pricing/page"));
const PrivacyPage = lazy(() => import("@/routes/privacy/page"));
const ReleasesPage = lazy(() => import("@/routes/releases/page"));
const ResourcesPage = lazy(() => import("@/routes/resources/page"));
const TermsPage = lazy(() => import("@/routes/terms/page"));
const ImageGeneratorPage = lazy(async () => ({ default: (await import("@/components/tools/image-generator/ImageGeneratorWorkspace")).ImageGeneratorWorkspace }));
// 与预览代理、工具目录共用同一套开关：production 同样注册这两个工作区。
const VideoSearchPage = isVideoSearchEnabled(import.meta.env.MODE)
  ? lazy(async () => ({ default: (await import("@/components/tools/video-search/VideoSearchWorkspace")).VideoSearchWorkspace }))
  : null;
const VideoAnalysisPage = isVideoAnalysisEnabled(import.meta.env.MODE)
  ? lazy(async () => ({ default: (await import("@/components/tools/video-analysis/VideoAnalysisWorkspace")).VideoAnalysisWorkspace }))
  : null;
const ToolPage = lazy(() => import("@/routes/tools/[slug]/page"));
const ToolsPage = lazy(() => import("@/routes/tools/page"));
const UseCasePage = lazy(() => import("@/routes/use-cases/[slug]/page"));
const UseCasesPage = lazy(() => import("@/routes/use-cases/page"));

export const appRoutes: RouteObject[] = [{ element: <RouteFrame />, errorElement: <RouteError />, children: [
  { index: true, element: <LandingPage /> },
  { path: "about", element: <AboutPage /> },
  { path: "agents", element: <AgentsPage /> },
  { path: "agents/:slug", element: <AgentPage /> },
  { path: "blog", element: <BlogPage /> },
  { path: "blog/:slug", element: <BlogPostPage /> },
  { path: "contact", element: <ContactPage /> },
  { path: "early-access", element: <EarlyAccessPage /> },
  { path: "help", element: <HelpPage /> },
  { path: "integrations", element: <IntegrationsPage /> },
  { path: "pricing", element: <PricingPage /> },
  { path: "privacy", element: <PrivacyPage /> },
  { path: "releases", element: <ReleasesPage /> },
  { path: "resources", element: <ResourcesPage /> },
  { path: "terms", element: <TermsPage /> },
  { path: "tools", element: <ToolsPage /> },
  { path: "tools/image-generator", element: <ImageGeneratorPage /> },
  ...(VideoSearchPage ? [{ path: "tools/video-search", element: <VideoSearchPage /> }] : []),
  ...(VideoAnalysisPage ? [{ path: "tools/video-analysis", element: <VideoAnalysisPage /> }] : []),
  { path: "tools/:slug", element: <ToolPage /> },
  { path: "use-cases", element: <UseCasesPage /> },
  { path: "use-cases/:slug", element: <UseCasePage /> },
  { path: "*", element: <NotFoundPage /> },
]}];

export const router = createBrowserRouter(appRoutes);
